import { useEffect, useRef } from "react";
import { useApp } from "../context/AppState";
import { POPUP_SECTIONS } from "../constant/constant";
import { useAnalytics } from "./useAnalytics";
import { trackImpression } from "../utils/analyticsUtils";
import { isPopupReadyForImpression } from "../utils/popup-runtime-content-loading";
import {
  getActiveStorefrontTrackingContext,
  setActiveStorefrontTrackingContext,
} from "../services/cart.service";
import { getStepSectionsForPopup } from "../utils/popup-step-sections";

/**
 * Storefront embedded upsells (product page slot, cart drawer strip) do not use
 * RenderPopupInstance — track impressions here and keep pixel context for add-to-cart.
 */
export default function useEmbeddedUpsellAnalytics({
  popupData,
  activeStep = 0,
  enabled = true,
  slotNo = null,
}) {
  const { appState } = useApp();
  const { addAnalyticsEvent } = useAnalytics();
  const impressedPopupIdRef = useRef(null);

  useEffect(() => {
    if (!enabled || appState?.is_preview_mode || !popupData?._id) {
      return undefined;
    }

    if (!isPopupReadyForImpression(popupData)) {
      return undefined;
    }

    const popupId = String(popupData._id);

    if (impressedPopupIdRef.current !== popupId) {
      impressedPopupIdRef.current = popupId;
      trackImpression({
        popupId: popupData._id,
        popupType: popupData.template_id?.title || popupData.popUpType,
        campaignId: popupData.campaign_Id,
        addAnalyticsEvent,
      });
    }

    const products = getStepSectionsForPopup(popupData, activeStep)
      .filter((section) => section?.type === POPUP_SECTIONS.PRODUCT)
      .flatMap((section) => section?.products || []);

    setActiveStorefrontTrackingContext({
      popup: popupData,
      products,
      shop: window?.Shopify?.shop || window.location.hostname,
      offerRuleId: popupData?.__weupsellMatchedOfferRuleId || undefined,
      ...(slotNo != null && slotNo !== "" ? { slotNo: String(slotNo) } : {}),
    });

    return () => {
      const active = getActiveStorefrontTrackingContext();
      if (active?.popup?._id === popupData._id) {
        setActiveStorefrontTrackingContext(null);
      }
    };
  }, [
    enabled,
    appState?.is_preview_mode,
    popupData,
    popupData?._id,
    popupData?.campaign_Id,
    popupData?.popUpType,
    popupData?.template_id?.title,
    activeStep,
    slotNo,
    addAnalyticsEvent,
  ]);
}
